import * as React from "react";
import styled from "styled-components";
import { Row } from "react-grid-system";
import { HorizontalSpacer } from "../atoms/Spacers";
import Body from "../atoms/typography/Body";
import LoadingSpinner from "../atoms/LoadingSpinner";
import { theme } from "../../theme";

interface Props {
  text: string;
  onDrop: (file: any) => void;
  multiple?: boolean;
}

const UploadContainer = styled.div<{ highlight?: boolean }>`
  border: 1px dashed ${theme.colors.primary};
  padding: 32px;
  border-radius: 4px;
  background-color: ${(props) =>
    props.highlight ? theme.colors.info20 : theme.colors.white};
  cursor: pointer;

  &:hover {
    background-color: ${theme.colors.info20};
  }
`;

const UploadFileIcon: React.FC = () => (
  <svg
    width="42"
    height="44"
    viewBox="0 0 42 44"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M26.9919 4.12891C21.2017 4.12891 16.1227 7.91194 14.1564 13.5031C13.169 13.073 12.2905 12.8799 10.8345 12.8799C5.57985 12.8799 1.3125 17.3651 1.3125 22.8685C1.3125 28.3807 5.57985 32.8572 10.8345 32.8572H17.3945C18.0807 32.8572 18.6496 32.2691 18.6496 31.5406C18.6496 30.812 18.0807 30.224 17.3945 30.224H10.8345C6.96883 30.224 3.8227 26.9237 3.8227 22.8685C3.8227 18.8134 6.96883 15.5131 10.8345 15.5131C12.3407 15.5131 12.8511 15.7062 14.3488 16.505C14.3572 16.5137 14.3572 16.5137 14.3572 16.5137C14.4074 16.5401 14.4576 16.5489 14.5078 16.5752C14.5413 16.584 14.5664 16.6015 14.5998 16.6103C14.6082 16.6103 14.6082 16.6103 14.6166 16.6103C14.6584 16.6279 14.6919 16.6279 14.7337 16.6279C14.7672 16.6366 14.809 16.6454 14.8509 16.6454C14.8927 16.6542 14.9429 16.6454 14.9847 16.6454C15.0182 16.6454 15.0517 16.6454 15.0935 16.6366C15.1354 16.6279 15.1772 16.6191 15.2274 16.6103C15.2609 16.6015 15.2943 16.5927 15.3278 16.5752C15.3696 16.5664 15.4031 16.5401 15.4449 16.5225C15.4784 16.505 15.5119 16.4962 15.5454 16.4786C15.5872 16.4523 15.6207 16.426 15.6541 16.3996C15.6792 16.3733 15.7127 16.3558 15.7462 16.3294C15.7796 16.2943 15.8047 16.2592 15.8382 16.2241C15.8633 16.1978 15.8884 16.1714 15.9135 16.1451C15.947 16.1012 15.9721 16.0485 15.9972 15.9959C16.0139 15.9783 16.0307 15.9608 16.039 15.9344C16.039 15.9257 16.039 15.9257 16.039 15.9257C16.0641 15.873 16.0809 15.8291 16.0976 15.7764C16.106 15.7413 16.1227 15.7062 16.1311 15.6711C16.1311 15.6623 16.1394 15.6623 16.1394 15.6536C17.3778 10.4135 21.846 6.76211 26.9919 6.76211C33.1586 6.76211 38.179 12.0197 38.179 18.4974C38.179 24.9663 33.1586 30.224 26.9919 30.224C26.2974 30.224 25.7368 30.812 25.7368 31.5406C25.7368 32.2691 26.2974 32.8572 26.9919 32.8572C34.5476 32.8572 40.6892 26.4146 40.6892 18.4974C40.6892 10.5715 34.5476 4.12891 26.9919 4.12891Z"
      fill="#0D5257"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M27.9367 27.5469C28.2547 27.5469 28.5726 27.424 28.8153 27.1695C29.309 26.6604 29.3173 25.8266 28.832 25.3087L23.1088 19.1997C22.8661 18.9539 22.5482 18.8047 22.2135 18.8047C21.8704 18.8047 21.5524 18.9539 21.3182 19.1997L15.6869 25.2297C15.2016 25.7476 15.21 26.5814 15.7037 27.0905C16.1973 27.5996 16.9922 27.5908 17.4775 27.073L20.8914 23.4128V38.9663C20.8914 39.686 21.452 40.2829 22.1465 40.2829C22.841 40.2829 23.4016 39.686 23.4016 38.9663V23.2811L27.0414 27.1519C27.2841 27.4153 27.6104 27.5469 27.9367 27.5469Z"
      fill="#0D5257"
    />
  </svg>
);

const UploadFile: React.FC<Props> = ({ text, onDrop, multiple = true }) => {
  const [loading, setLoading] = React.useState(false);
  const [isDragOver, setIsDragOver] = React.useState(false);

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setLoading(true);
    const uploadedFiles = Array.from(event.dataTransfer.files).filter(
      (file) => file.type === "application/pdf"
    );
    setLoading(false);
    setIsDragOver(false);

    onDrop(uploadedFiles);
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click(); // Trigger the file input click
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []).filter(
      (file) => file.type === "application/pdf"
    );

    onDrop(selectedFiles);
  };

  return (
    <div>
      <UploadContainer
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        highlight={isDragOver}
        onClick={handleClick}
      >
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          onChange={handleFileChange}
          multiple={multiple}
          accept="application/pdf"
        />
        <div>
          <Row justify="center">
            <UploadFileIcon />
          </Row>
          <HorizontalSpacer spacing={1} />
          <Body textalign="center">{text}</Body>
          {loading && <LoadingSpinner />}
        </div>
      </UploadContainer>
    </div>
  );
};

export default UploadFile;
